import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  getKeyValue,
} from "@heroui/react";
import {
  Plus,
  Trash,
  FileText,
  Eye,
  Check,
  FilePlus,
  ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { BuildingDTO } from "shared/types/building";

interface ServiceTabProps {
  building: BuildingDTO;
}

export const ServiceTab = ({ building }: ServiceTabProps) => {
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isWerkbonModalOpen, setIsWerkbonModalOpen] = useState(false);
  const [isAddLineModalOpen, setIsAddLineModalOpen] = useState(false);
  const [sortDescriptor, setSortDescriptor] = useState<any>({
    column: "name",
    direction: "ascending",
  });

  useEffect(() => {
    console.log(building);
  }, [building]);

  const [data, setData] = useState([
    {
      name: "Service A",
      status: "Actief",
      nextInspection: "20/01/2024",
      documents: "docs_link_A",
    },
    {
      name: "Service B",
      status: "Inactief",
      nextInspection: "15/06/2024",
      documents: "docs_link_B",
    },
  ]);

  const handleOpenDetailModal = () => setIsDetailModalOpen(true);
  const handleCloseDetailModal = () => setIsDetailModalOpen(false);
  const handleOpenAddLineModal = () => setIsAddLineModalOpen(true);
  const handleCloseAddLineModal = () => setIsAddLineModalOpen(false);

  const handleSortChange = (descriptor: any) => {
    setSortDescriptor(descriptor);
    const sortedData = [...data].sort((a, b) => {
      let first = getKeyValue(a, descriptor.column);
      let second = getKeyValue(b, descriptor.column);

      if (descriptor.direction === "ascending") {
        return first < second ? -1 : first > second ? 1 : 0;
      } else {
        return first > second ? -1 : first < second ? 1 : 0;
      }
    });

    setData(sortedData);
  };

  return (
    <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
      <div className="flex flex-col gap-2.5 sm:col-span-2">
        <header className="flex items-center justify-between">
          <div className="5 flex items-center gap-1 text-lg font-medium">
            <ArrowsCounterClockwise weight="duotone" size={20} /> Onderhoud
          </div>
          <div className="flex items-center gap-4">
            {selectedKeys.length > 0 && (
              <ButtonNew
                icon={<FilePlus />}
                label="Genereer werkbon"
                key="generate-workbon"
                variant="primaryMoss"
                onClick={() => setIsWerkbonModalOpen(true)}
              />
            )}
            <ButtonNew
              icon={<Plus />}
              label="Lijn toevoegen"
              variant="primaryMoss"
              onClick={handleOpenAddLineModal}
            />
          </div>
        </header>
        <Table
          aria-label="Service Table"
          selectionMode="multiple"
          onSelectionChange={(keys) => setSelectedKeys(Array.from(keys))}
          color="success"
          sortDescriptor={sortDescriptor}
          onSortChange={handleSortChange}
          classNames={{
            wrapper: "shadow-none rounded-none p-0",
            thead: "h-fit border-b-2 border-neutral-primary/40",
            th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
            tbody: "border-b divide-y divide-neutral-primary/15",
            tr: "divide-x divide-neutral-primary/15",
            td: "py-2.5",
          }}
        >
          <TableHeader>
            <TableColumn key="name" allowsSorting>
              Naam
            </TableColumn>
            <TableColumn key="status" allowsSorting>
              Status
            </TableColumn>
            <TableColumn key="nextInspection" allowsSorting>
              Volgende keuring
            </TableColumn>
            <TableColumn key="documents">Documenten</TableColumn>
            <TableColumn key="actions">Acties</TableColumn>
          </TableHeader>
          <TableBody emptyContent={<p>No rows to display.</p>}>
            {data.map((item) => (
              <TableRow key={item.name}>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.status}</TableCell>
                <TableCell>{item.nextInspection}</TableCell>
                <TableCell>
                  <Tooltip content="Bekijk documenten">
                    <a href="#">
                      <FileText size={22} weight="duotone" />
                    </a>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Tooltip content="Bekijk details">
                      <a
                        href="javascript:void(0);"
                        onClick={handleOpenDetailModal}
                      >
                        <Eye size={22} weight="duotone" />
                      </a>
                    </Tooltip>
                    <Tooltip content="Verwijder service">
                      <a href="#">
                        <Trash
                          size={22}
                          weight="duotone"
                          className="text-status-bad"
                        />
                      </a>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Modals for Werkbon, Service Details, and Add Line */}
      <Modal
        isOpen={isWerkbonModalOpen}
        onClose={() => setIsWerkbonModalOpen(false)}
        size="5xl"
        shouldBlockScroll={true}
        scrollBehavior="inside"
        backdrop="blur"
        className="p-8"
      >
        <ModalContent className="flex flex-col gap-4 font-normal text-neutral-primary">
          <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
            Werkbon Aanmaken
            <ButtonNew
              variant="primaryMoss"
              onClick={() => setIsWerkbonModalOpen(false)}
              icon={<Check />}
              label="Werkbon Aanmaken"
            />
          </ModalHeader>
          <ModalBody className="grid gap-5 gap-y-8 px-0 sm:grid-cols-2">
            <div>
              <label
                htmlFor="werkbon-description"
                className="block text-sm font-medium text-neutral-primary"
              >
                Werkbon Beschrijving
              </label>
              <textarea
                id="werkbon-description"
                className="w-full rounded-sm border border-neutral-primary bg-white p-2 text-neutral-primary"
              ></textarea>
            </div>
            <div>
              <label
                htmlFor="werkbon-date"
                className="block text-sm font-medium text-neutral-primary"
              >
                Datum Uitvoering
              </label>
              <input
                type="date"
                id="werkbon-date"
                className="w-full rounded-sm border border-neutral-primary bg-white p-2 text-neutral-primary"
              />
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        size="5xl"
        shouldBlockScroll={true}
        scrollBehavior="inside"
        backdrop="blur"
        className="p-8"
      >
        <ModalContent className="flex flex-col gap-4 font-normal text-neutral-primary">
          <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
            Service Details
            <ButtonNew
              variant="primaryMoss"
              onClick={handleCloseDetailModal}
              icon={<Check />}
              label="Wijzigingen bewaren"
            />
          </ModalHeader>
          <ModalBody className="grid gap-5 gap-y-8 px-0 sm:grid-cols-2">
            <div>
              <label
                htmlFor="status-select"
                className="block text-sm font-medium text-neutral-primary"
              >
                Service Status
              </label>
              <select
                id="status-select"
                className="w-full rounded-sm border border-neutral-primary bg-white p-2 text-neutral-primary"
              >
                <option value="active">Actief</option>
                <option value="inactive">Inactief</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="next-inspection"
                className="block text-sm font-medium text-neutral-primary"
              >
                Volgende Keuring
              </label>
              <input
                type="date"
                id="next-inspection"
                className="w-full rounded-sm border border-neutral-primary bg-white p-2 text-neutral-primary"
              />
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isAddLineModalOpen}
        onClose={handleCloseAddLineModal}
        size="5xl"
        shouldBlockScroll={true}
        scrollBehavior="inside"
        backdrop="blur"
        className="p-8"
      >
        <ModalContent className="flex flex-col gap-4 font-normal text-neutral-primary">
          <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
            Lijn Toevoegen
            <ButtonNew
              variant="primaryMoss"
              onClick={handleCloseAddLineModal}
              icon={<Check />}
              label="Lijn Toevoegen"
            />
          </ModalHeader>
          <ModalBody className="grid gap-5 gap-y-8 px-0 sm:grid-cols-2">
            <div>
              <label
                htmlFor="line-name"
                className="block text-sm font-medium text-neutral-primary"
              >
                Naam
              </label>
              <input
                type="text"
                id="line-name"
                className="w-full rounded-sm border border-neutral-primary bg-white p-2 text-neutral-primary"
              />
            </div>
            <div>
              <label
                htmlFor="line-status"
                className="block text-sm font-medium text-neutral-primary"
              >
                Status
              </label>
              <select
                id="line-status"
                className="w-full rounded-sm border border-neutral-primary bg-white p-2 text-neutral-primary"
              >
                <option value="active">Actief</option>
                <option value="inactive">Inactief</option>
              </select>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ServiceTab;
